import React, { Fragment, memo, useRef, useEffect, useState, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import { getDiscountLabel } from "src/utils";
import {
  couponDetailLabel,
  promotionAdjustmentLabel,
} from "src/helper/promotionItemAttribution";
import { valueFormat } from "src/helper/valueFormat";
import {
  DiscountRowLabelWrapper,
  DiscountRowPriceWrapper,
  DiscountRowTextWrapper,
  NamePriceRowWrapper,
  DiscountRowWrapper,
  MofiferNameWrapper,
  MofiferOptionsNameWrapper,
  MofiferOptionsTextWrapper,
  MofiferOptionsPriceText,
  MofiferOptionsText
} from "./styles";
import { PRODUCT_STATUS, ORDER_STATUSES } from "src/constants";

const styles = {
  row: {
    marginBottom: 0,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  countCell: {
    textAlign: "left",
  },
  nameCell: {
    textAlign: "left",
    padding: "0 14px",
    fontSize: "14px",
    fontWeight: 600,
  },
  priceCell: {
    textAlign: "right",
  },
};

const OrderProduct = ({
  product,
  charges,
  promotions = [],
  showVoidItemReceipt = true,
}) => {
  const {
    count,
    name,
    modifierGroup,
    singlePriceWithoutModifier,
    price,
  } = product || {}

  const containerRef = useRef(null);
  const priceRef = useRef(null);
  const modPriceRef = useRef(null);
  const [nameWidth, setNameWidth] = useState("100%");
  const [modPriceWidth, setModPriceWidth] = useState("100%");

  const itemDiscount = useMemo(() => {
    return charges?.discountDetails?.find((item) => item?.appliesTo === 'item' && item?.itemUuid === product?.uuid);
  }, [charges?.discountDetails, product?.uuid]);

  const itemCoupon = itemDiscount?.couponId ? itemDiscount : null;

  const visiblePromotions = useMemo(() => {
    const coveredLabels = new Set(
      (charges?.discountDetails || [])
        .filter(
          (item) =>
            item?.appliesTo === 'item' &&
            item?.itemUuid === product?.uuid &&
            item?.couponId
        )
        .flatMap((item) => [
          item.couponTitle,
          item.couponName,
          item.promotionName,
          item.discountCode,
          item.couponCode,
        ])
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean)
    );

    return (promotions || []).filter((adjustment) => {
      const labels = [
        adjustment.displayLabel,
        adjustment.promotionName,
        adjustment.promotionCode,
      ]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean);
      return !labels.some((label) => coveredLabels.has(label));
    });
  }, [charges?.discountDetails, product?.uuid, promotions]);

  const mainPrice = valueFormat(`$${Number(singlePriceWithoutModifier || 0).toFixed(2)}`);
  const discountPrice = itemDiscount?.discountAmount
    && valueFormat(`$${Number((price || 0) - (itemDiscount?.discountAmount || 0)).toFixed(2)}`)
  const isVoid = product?.status?.name === PRODUCT_STATUS.VOID;
  const itemNote = product?.notes || product?.instructions || '';

  useEffect(() => {
    if (!containerRef.current || !priceRef.current) return;

    const update = () => {
      const container = containerRef.current;
      const price = priceRef.current;
      const containerWidth = container?.offsetWidth;
      const priceWidth = price?.offsetWidth;
      const available = containerWidth - priceWidth; // include safe padding
      const modPriceWidth = modPriceRef?.current?.offsetWidth;
      setNameWidth(`${available}px`);
      setModPriceWidth(`${modPriceWidth - 32}px`);
    };

    update(); // run once initially

    const resizeObserver = new ResizeObserver(() => {
      update(); // update anytime size changes
    });

    resizeObserver.observe(containerRef.current);
    resizeObserver.observe(priceRef.current);

    return () => resizeObserver.disconnect();
  }, [containerRef?.current, priceRef?.current, modPriceRef?.current]);
   
  const shouldShowStatusDisplayName = useMemo(() => {
    return product?.status?.name === ORDER_STATUSES.COMP_AFTER_TXN ||
        product?.status?.name === ORDER_STATUSES.COMP_BEFORE_TXN;
  }, [product?.status?.name]);

  // If item is void and showVoidItemReceipt is false, don't render the item
  if (isVoid && !showVoidItemReceipt) {
 
    return null;
  }

  return (
    <>
      <Box sx={styles.row} ref={containerRef} >
        <NamePriceRowWrapper sx={{ maxWidth: nameWidth, textDecoration: isVoid ? 'line-through' : 'none' }}>
          <Typography sx={styles.countCell}>{`${count}x`}</Typography><Typography
            className="uppercase"
            noWrap
            sx={{
              ...styles.nameCell,
              maxWidth: nameWidth,
            }}
          >
            {name?.en}
          </Typography>
        </NamePriceRowWrapper>

        <Box ref={priceRef}>
          <Typography
            className="uppercase"
            sx={{
              textDecoration: itemDiscount?.discountAmount > 0 || isVoid ? "line-through" : "none",
              width: priceRef?.current?.offsetWidth,
            }}
          >
            {mainPrice}
          </Typography>
        </Box>
      </Box>
       {shouldShowStatusDisplayName && product?.status?.displayName ? (
        <Typography sx={{ fontSize: '14px', color: 'gray', fontStyle: 'italic' }}>
          ({product?.status?.displayName})
        </Typography>
      ) : null}
      {modifierGroup?.length > 0 &&
        modifierGroup.map((mod) => (
          <Fragment key={mod?._id}>
            <MofiferNameWrapper
              className="modifier-main-name modifier-name-longer"
              sx={{ textDecoration: isVoid ? 'line-through' : 'none' }}
            >
              {mod?.name?.en}
            </MofiferNameWrapper>
        
            {mod?.modifierOptions?.map((item) => (
              <MofiferOptionsNameWrapper key={item?._id} sx={{ textDecoration: isVoid ? 'line-through' : 'none' }}>
                <MofiferOptionsTextWrapper sx={{ maxWidth: nameWidth }}>
                  <MofiferOptionsText noWrap>
                    {item?.count ? <b>{`${item?.count}x `}</b> : null}
                    <span>{item?.name?.en}</span>
                    {item?.freeCount ? <b>{` (${item?.freeCount} free)`}</b> : null}
                  </MofiferOptionsText>
                </MofiferOptionsTextWrapper>

                <MofiferOptionsPriceText ref={modPriceRef} sx={{ textDecoration: isVoid || itemDiscount?.discountAmount > 0 ? 'line-through' : 'none' }}>
                  {item?.isFree
                    ? "-"
                    : valueFormat(`$${Number(item?.totalPrice || 0).toFixed(2)}`)}
                </MofiferOptionsPriceText>
              </MofiferOptionsNameWrapper>
            ))}
          </Fragment>
        ))}

      {itemCoupon?.discountAmount > 0 ? (
        <DiscountRowWrapper>
          <DiscountRowTextWrapper
            sx={{ textDecoration: isVoid ? "line-through" : "none" }}
          >
            <DiscountRowLabelWrapper
              sx={{
                ...styles.nameCell,
                fontSize: "16px",
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {couponDetailLabel(itemCoupon)}
              </span>
            </DiscountRowLabelWrapper>
          </DiscountRowTextWrapper>
          <DiscountRowPriceWrapper
            sx={{
              ...styles.priceCell,
              alignItems: "baseline",
              fontSize: "16px",
            }}
          >
            <span style={{ fontWeight: 500 }}>
              {valueFormat(
                `($${Number(itemCoupon.discountAmount || 0).toFixed(2)})`
              )}
            </span>
          </DiscountRowPriceWrapper>
        </DiscountRowWrapper>
      ) : itemDiscount?.discountAmount > 0 ? (
        <DiscountRowWrapper>
          <DiscountRowTextWrapper sx={{ textDecoration: isVoid ? 'line-through' : 'none' }}>
            <DiscountRowLabelWrapper
              sx={{
                ...styles.nameCell,
                fontSize: '16px',
              }}
            >
              <span style={{ fontWeight: 500, }}>
                Discount {getDiscountLabel(itemDiscount)}
              </span>
            </DiscountRowLabelWrapper>
          </DiscountRowTextWrapper>

          <DiscountRowPriceWrapper
            sx={{
              ...styles.priceCell,
              alignItems: 'baseline',
              fontSize: '16px',
            }}
          >
            <span style={{ fontWeight: 500 }}>{discountPrice}</span>
          </DiscountRowPriceWrapper>
        </DiscountRowWrapper>
      ) : null}
      {visiblePromotions.map((adjustment, index) => (
        <DiscountRowWrapper
          key={`${adjustment.promotionCode || adjustment.promotionName || 'promotion'}-${index}`}
        >
          <DiscountRowTextWrapper sx={{ textDecoration: isVoid ? 'line-through' : 'none' }}>
            <DiscountRowLabelWrapper
              sx={{
                ...styles.nameCell,
                fontSize: '16px',
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {promotionAdjustmentLabel(adjustment)}
              </span>
            </DiscountRowLabelWrapper>
          </DiscountRowTextWrapper>
          <DiscountRowPriceWrapper
            sx={{
              ...styles.priceCell,
              alignItems: 'baseline',
              fontSize: '16px',
            }}
          >
            <span style={{ fontWeight: 500 }}>
              {valueFormat(
                `($${Number(adjustment.discountAmount || 0).toFixed(2)})`
              )}
            </span>
          </DiscountRowPriceWrapper>
        </DiscountRowWrapper>
      ))}
      {itemNote ? (
        <Box>
          <Box sx={{ marginLeft: '30px' }}>
            <Typography style={{ textDecoration: isVoid ? 'line-through' : 'none' }}>NOTES:</Typography>
            {itemNote?.split('\n')?.map((item, index) => {
              if (!item) return null;
              return (
                <React.Fragment key={index}>
                  <Typography sx={{ maxWidth: '360px', textDecoration: isVoid ? 'line-through' : 'none' }}>{item}</Typography>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      ) : null}
    </>
  );
};

export default memo(OrderProduct);
